import React, { lazy, useEffect, Suspense, useState } from "react";
import { ThemeProvider } from "styled-components";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { GlobalStyles } from "./styles/Global";
import PrivateRoute from "./auth/privateRoute";
import PublicRoute from "./auth/publicRoute";
import ScrollToTop from "./utils/ScrollReset";
import NoMatch from "./NoMatch";
import { handleHideErrorModal, handleHideRetryModal } from "./redux/actions/loadingAction";
import ErrorModal from "./components/AuthenticatedApp/components/modals/ErrorPage";
import { Portal } from "./components/AuthenticatedApp/components/modals/Portal";
import Retry from "./components/AuthenticatedApp/components/modals/Retry";
import Loader from "./library/Loader";
import Login from "./components/PublicApp/views/signIn/Login";
import { intializeDatadog } from "./eventLogger";
import "./bootstrap";
import useIp from "./utils/_hooks/useIp";
import SalesModeRoute from "./auth/SalesModeRoute";
import SalesModeMainRoute from "./components/SalesModeApp/routes/SalesMode.routes";
import CreateAPasskey from "./components/PublicApp/views/CreateAPasskey";

const CreateBusinessAccountView = lazy(() =>
    import(
        /* webpackChunkName: 'GetStarted_chunk' */
        /* webpackPrefetch: true */
        "./components/PublicApp/layout/Onboarding/CreateBusinessAccountView"
    ),
);

const ValidateOTPView = lazy(() =>
    import(
        /* webpackChunkName: 'ValidateOTPVIEW_chunk' */
        /* webpackPrefetch: true */
        "./components/PublicApp/views/signIn/ValidateLogin"
    ),
);

const EmailSignupView = lazy(() =>
    import(
        /* webpackChunkName: 'EmailSignUpView_chunk' */
        /* webpackPrefetch: true */
        "./components/PublicApp/layout/NewToBank/SignupView"
    ),
);

const SelectProfileView = lazy(() =>
    import(
        /* webpackChunkName: 'SelectProfile_chunk' */
        /* webpackPrefetch: true */
        "./components/PublicApp/views/SelectProfiles"
    ),
);

const PasswordReset = lazy(() =>
    import(
        /* webpackChunkName: 'PasswordReset_chunk' */
        /* webpackPrefetch: true */
        "./components/PublicApp/views/passwordReset"
    ),
);

const AboutYouView = lazy(() =>
    import(
        /* webpackChunkName: 'AboutYouView_chunk' */
        /* webpackPrefetch: true */
        "./components/PublicApp/layout/NewToBank/AboutYouView"
    ),
);

const RetailSignup = lazy(() =>
    import(
        /* webpackChunkName: 'RetailSignUp_chunk' */
        /* webpackPrefetch: true */
        "./components/PublicApp/layout/Onboarding/RetailSignup"
    ),
);

const EstimateRoutes = lazy(() =>
    import(
        /* webpackChunkName: 'Estimate_chunk' */
        "./components/PublicApp/routes/Estimate.routes"
    ),
);

const InviteSignatoryRoute = lazy(() =>
    import(
        /* webpackChunkName: 'InvitedSignatory_chunk' */
        "./components/PublicApp/routes/Invite.routes"
    ),
);

const ValidateSignup = lazy(() =>
    import(
        /* webpackChunkName: 'ValidateSignUp_chunk' */
        /* webpackPrefetch: true */
        "./components/PublicApp/layout/Onboarding/ValidateSignup"
    ),
);

const SignupViaAppLayout = lazy(() => import("./components/PublicApp/layout/signin/SignupViaApp"));

const ConsentRoute = lazy(() =>
    import(
        /* webpackChunkName: 'ConsentRoutes_chunk' */
        "./components/PublicApp/routes/Consent.routes"
    ),
);

const Routes = lazy(() =>
    import(
        /* webpackChunkName: 'MainRoutes_chunk' */
        "./components/AuthenticatedApp/routes/Main.routes"
    ),
);

const SignInViaSalesMode = lazy(() => import("./components/PublicApp/views/SignInViaSalesMode"));

function App() {
    useIp();

    const { showErrorModal, showRetryModal } = useSelector(({ loadingState }) => loadingState);
    const [theme] = useState("light");
    const dispatch = useDispatch();

    const intercomInit = () => {
        window.Intercom("boot", {
            api_base: process.env.REACT_APP_INTERCOM_API_BASE_URL,
            app_id: process.env.REACT_APP_INTERCOM_APP_ID,
            //Website visitor so may not have any user related info
        });
    };

    useEffect(() => {
        /* A function that initializes the datadog logger. */
        intializeDatadog();
        intercomInit();
    }, []);

    return (
        <>
            <ThemeProvider theme={{ theme }}>
                <GlobalStyles />
                <Router>
                    <ScrollToTop />
                    <Switch>
                        <PublicRoute exact path="/login" component={Login} />
                        <PublicRoute exact path="/validate-otp" component={ValidateOTPView} />
                        <PublicRoute path="/getKuda" component={SignupViaAppLayout} />
                        <Route
                            path="/estimate/preview"
                            render={(routeProps) => (
                                <Suspense fallback={<Loader />}>
                                    <EstimateRoutes {...routeProps} />
                                </Suspense>
                            )}
                        />

                        <Route
                            path="/invite"
                            render={(routeProps) => (
                                <Suspense fallback={<Loader />}>
                                    <InviteSignatoryRoute {...routeProps} />
                                </Suspense>
                            )}
                        />
                        <PublicRoute path="/verify-signup" component={ValidateSignup} />
                        <PublicRoute path="/get-started" component={CreateBusinessAccountView} />
                        <PublicRoute exact path="/select-profile" component={SelectProfileView} />
                        <PublicRoute path="/create-passkey" component={CreateAPasskey} />
                        <PublicRoute path="/new-signup" component={EmailSignupView} />
                        <PublicRoute path="/about-you" component={AboutYouView} />
                        <PublicRoute path="/retail-signup" component={RetailSignup} />
                        <PublicRoute path={`/reset-password`} component={PasswordReset} />
                        <PublicRoute path={`/consent`} component={ConsentRoute} />
                        <PublicRoute path={`/sales-mode-login`} component={SignInViaSalesMode} />
                        <SalesModeRoute path="/sales" component={SalesModeMainRoute} />
                        <PrivateRoute path="/" component={Routes} />
                        <Route path="*">
                            <NoMatch />
                        </Route>
                    </Switch>
                </Router>
            </ThemeProvider>

            {showErrorModal && (
                <Portal>
                    <ErrorModal onClose={() => dispatch(handleHideErrorModal())} />
                </Portal>
            )}

            {showRetryModal && (
                <Portal>
                    <Retry onClose={() => dispatch(handleHideRetryModal())} />
                </Portal>
            )}
        </>
    );
}

export default App;
